import { type ReactNode } from 'react';
import { calendarFeedUrls, copyToClipboard, isLocalOrigin } from '@bala/core';
import { Badge, Button, Input } from './primitives';
import { Modal, useToast } from './overlays';
import { IconCalendar } from './icons';

/**
 * Fenêtre d'abonnement au calendrier, partagée par les deux applications.
 *
 * Les familles et le staff s'abonnent au même geste près : seul le flux derrière
 * le lien diffère. Écrite deux fois, la marche à suivre pour iPhone aurait fini
 * par diverger d'une application à l'autre.
 *
 * Un fichier .ics téléchargé est une photographie ; un abonnement est relu
 * régulièrement par l'agenda. C'est la seule forme qui reste juste dans le
 * temps.
 */
export interface CalendarSubscribeModalProps {
  open: boolean;
  onClose: () => void;
  /** Secret du flux. `null` tant qu'il n'est pas chargé. */
  token: string | null;
  title?: string;
  subtitle?: string;
  /** Message d'erreur si le jeton n'a pas pu être obtenu. */
  error?: string | null;
  /** Affiché sous le lien : à qui ce lien donne accès, et à quoi. */
  scopeNote?: ReactNode;
  /** Fourni si l'utilisateur peut révoquer le lien. */
  onRotate?: () => void;
  rotating?: boolean;
  /** Adresse de l'API (pour forcer le bon domaine en production). */
  apiUrl?: string;
}

function Notice({ tone, children }: { tone: 'orange' | 'red'; children: ReactNode }) {
  return (
    <p
      style={{
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-md)',
        background: `var(--${tone}-soft)`,
        color: `var(--${tone})`,
        font: 'var(--t-footnote)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </p>
  );
}

export function CalendarSubscribeModal({
  open,
  onClose,
  token,
  title = 'Suivre le calendrier',
  subtitle = 'Les activités apparaissent dans votre agenda et s’y mettent à jour toutes seules.',
  error = null,
  scopeNote,
  onRotate,
  rotating = false,
  apiUrl,
}: CalendarSubscribeModalProps) {
  const toast = useToast();
  const urls = token ? calendarFeedUrls(token, apiUrl) : null;

  const copy = async () => {
    if (!urls) return;
    const done = await copyToClipboard(urls.https);
    toast[done ? 'success' : 'error'](
      done ? 'Lien copié.' : 'Copie refusée par le navigateur — sélectionnez le lien à la main.',
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width={520}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
        {error && <Notice tone="red">{error}</Notice>}

        {isLocalOrigin() && (
          <Notice tone="orange">
            Cette adresse n’existe que sur votre réseau. Un agenda en ligne relit le lien depuis ses propres
            serveurs : l’abonnement ne fonctionnera qu’une fois l’application accessible depuis Internet.
          </Notice>
        )}

        {urls && (
          <>
            <a href={urls.webcal} style={{ textDecoration: 'none' }}>
              <Button variant="primary" block icon={<IconCalendar size={16} />}>
                Ajouter à mon agenda
              </Button>
            </a>

            <div>
              <p style={{ font: 'var(--t-footnote)', fontWeight: 600, marginBottom: 6 }}>
                Ou copiez le lien dans votre agenda
              </p>
              <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
                {/* Lecture seule mais sélectionnable : si la copie automatique
                    est refusée, il reste possible de prendre le lien à la main. */}
                <Input
                  value={urls.https}
                  readOnly
                  aria-label="Lien d’abonnement au calendrier"
                  onFocus={(event) => event.currentTarget.select()}
                  style={{ flex: '1 1 220px' }}
                />
                <Button variant="secondary" onClick={copy}>
                  Copier
                </Button>
                {onRotate && (
                  <Button variant="secondary" tone="red" onClick={onRotate} loading={rotating}>
                    Régénérer
                  </Button>
                )}
              </div>
            </div>

            {scopeNote && (
              <p style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {scopeNote}
              </p>
            )}

            <div style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <p style={{ marginBottom: 4 }}>
                <Badge size="sm" tone="gray">
                  iPhone
                </Badge>{' '}
                Réglages → Calendrier → Comptes → Ajouter un compte → Autre → Ajouter un abonnement.
              </p>
              <p style={{ marginBottom: 4 }}>
                <Badge size="sm" tone="gray">
                  Google
                </Badge>{' '}
                Google Agenda sur ordinateur → Autres agendas → À partir de l’URL.
              </p>
              <p>
                Les agendas relisent le lien quand ils le décident, en général une à plusieurs fois par jour : un
                changement d’horaire peut mettre quelques heures à apparaître.
              </p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
